import React, {
  useState,
  useEffect,
} from 'react';
import { Box } from '@mui/material';

import { useDispatch, useSelector } from 'react-redux';
import { useGetTileLayersUrls } from '../Helpers/hooks';
import { MapStyleType, RootState } from '../../../types/types';
import { setSelectedMapStyle } from 'features/advancedMapState';

import standardImg from '../../../assets/images/map-style-standard.png';
import bingGreyImg from '../../../assets/images/map-style-bing-grey.png';
import humanitarianImg from '../../../assets/images/map-style-humanitarian.png';
import { bingLayerOptions } from 'utils/Map/mapUtils';

import 'assets/styles/MarkerTable.scss';

interface LayerStylesProps {
  L: any;
  mapRef: any;
  tileLayerRef: React.MutableRefObject<any>;
  createTileLayer: (url: string, options?: any) => any;
  selectedMapStyle: React.MutableRefObject<MapStyleType>;
}

interface LayerStylesCardProps {
  imgSrc: string;
  selected: boolean;
  mapStyleType: MapStyleType;
  handleCardsClick: (type: MapStyleType) => void;
}

interface LayerStylesCardRef {
  isSelected: boolean;
  handleSelectStyle: (isSelected: boolean) => void;
}

const LayerMapStylesCard: React.FC<LayerStylesCardProps> = ({ mapStyleType, imgSrc, selected, handleCardsClick }) => {
  const [isSelected, setIsSelected] = useState<boolean>(false);

  useEffect(() => {
    if (selected != isSelected) {
      setIsSelected(selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  return (
    <div className="layers-map-style-card">
      <div className="layers-map-style-card-title">{mapStyleType}</div>
      <div onClick={() => handleCardsClick(mapStyleType)}>
        <img
          src={imgSrc}
          width={'44'}
          height={'44'}
          alt={mapStyleType}
          className={`layers-map-style-card-img ${
            isSelected ? 'layers-map-style-card-img-selected' : ''
          }`}
        />
      </div>
    </div>
  );
};

const LayerMapStyles: React.FC<LayerStylesProps> = ({
  L,
  mapRef,
  tileLayerRef,
  createTileLayer,
  selectedMapStyle,
}) => {
  const dispatch = useDispatch();

  const { advancedMap } = useSelector((state: RootState) => state);

  const { mapStyles } = advancedMap;

  const TILE_LAYER_URLS = useGetTileLayersUrls();

  const mapStyleImages: Record<MapStyleType, string> = {
    Standard: standardImg,
    Humanitarian: humanitarianImg,
    'Bing Grey': bingGreyImg,
  };

  const updateSelectedCard = (
    selectedLayer: MapStyleType,
  ) => {
    dispatch(setSelectedMapStyle(selectedLayer));
  };

  const handleCardsClick = (type: MapStyleType) => {
    if (!mapRef.current) {
      console.error('Map reference is not defined.');
      return;
    }

    // Remove the current active layer if it exists
    if (tileLayerRef.current) {
      mapRef.current.removeLayer(tileLayerRef.current);
    }

    updateSelectedCard(type);
    switch (type.toLowerCase()) {
      case 'standard':
        tileLayerRef.current = createTileLayer(TILE_LAYER_URLS.standard);
        break;
      case 'bing grey':
        tileLayerRef.current = L.tileLayer.bing({
          ...bingLayerOptions,
          bingMapsKey: TILE_LAYER_URLS.bingGreyApiKey,
        });
        break;
      case 'humanitarian':
        tileLayerRef.current = createTileLayer(TILE_LAYER_URLS.humanitarian);
        break;
      default:
        console.warn('Invalid layer selected.');
        return;
    }

    selectedMapStyle.current = type;

    // Add the newly created tile layer to the map
    tileLayerRef.current?.addTo(mapRef.current);
  };

  const checkMapStyleSelected = (type: MapStyleType) => {
    if (mapStyles.selected == type) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    selectedMapStyle.current = mapStyles.selected;
    return () => {
      dispatch(setSelectedMapStyle(selectedMapStyle.current));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateSelectedCard(mapStyles.selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStyles.selected]);

  return (
    <Box className="layers-map-style-container">
      {mapStyles.options.map((option) => (
        <LayerMapStylesCard
          key={option}
          mapStyleType={option}
          imgSrc={mapStyleImages[option]}
          handleCardsClick={handleCardsClick}
          selected={checkMapStyleSelected(option)}
        />
      ))}
    </Box>
  );
};

export default LayerMapStyles;
